import React, {useMemo} from 'react';
import {
    Card,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Typography,
    Unstable_Grid2 as Grid,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import Dialog from '../../../../../../components/base/dialog';
import Button from '../../../../../../components/base/button/button';
import StatusPill from '../../components/status-pill';

const PointsReportDialog = ({open, customer, onClose}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.loyalty.customers'});

    const metrics = useMemo(() => {
        if (!customer) {
            return [];
        }
        return [
            {key: 'earned', label: t('earned'), value: customer.earned, status: 'Confirmed'},
            {key: 'confirmedBiz', label: t('confirmedAvailable'), value: customer.confirmedBiz, status: 'Confirmed'},
            {key: 'redeemed', label: t('redeemed'), value: customer.redeemed, status: 'claimed'},
            {key: 'pending', label: t('pending'), value: customer.pending, status: 'unclaimed'},
            {key: 'frozen', label: t('frozen'), value: customer.frozen, status: 'Frozen'},
            {key: 'reversed', label: t('reversed'), value: customer.reversed, status: 'Reversed'},
            {key: 'review', label: t('review'), value: customer.review, status: 'In review'},
        ];
    }, [customer, t]);

    return (
        <Dialog
            open={open}
            setOpen={(nextOpen) => {
                if (!nextOpen) {
                    onClose?.();
                }
            }}
            maxWidth="md"
        >
            <DialogTitle component="div">
                <Stack spacing={0.5} sx={{px: 2, pt: 2, pb: 1}}>
                    <Typography variant="h6">{t('reportTitle', {name: customer?.name})}</Typography>
                    {customer && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                                {t('customerNo')}: {customer.customerNo}
                            </Typography>
                            <StatusPill
                                flag={customer.pending > 0}
                                status={customer.pending > 0 ? 'unclaimed' : 'claimed'}
                                label={customer.pending > 0 ? t('unclaimed') : t('claimed')}
                            />
                        </Stack>
                    )}
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                {customer && (
                    <Stack spacing={1.5} sx={{pt: 1}}>
                        <Typography variant="subtitle2" color="text.secondary">
                            {t('reportSummary')}
                        </Typography>
                        <Grid container spacing={1.5}>
                            {metrics.map((metric) => (
                                <Grid key={metric.key} xs={12} sm={6} md={4}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            padding: 2,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                        }}
                                    >
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                            <Typography variant="body2" fontWeight={500}>
                                                {metric.label}
                                            </Typography>
                                            <StatusPill status={metric.status}/>
                                        </Stack>
                                        <Typography
                                            variant="body1"
                                            fontWeight={600}
                                            color={metric.key === 'reversed' ? 'error.main' : 'secondary.main'}
                                        >
                                            {metric.key === 'reversed' && Number(metric.value) > 0 ? '−' : ''}
                                            {Number(metric.value ?? 0).toLocaleString()}
                                        </Typography>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                        <Typography variant="caption" color="text.secondary">
                            {t('privacyNote')}
                        </Typography>
                    </Stack>
                )}
            </DialogContent>
            <Divider/>
            <DialogActions sx={{p: 0, m: 0, justifyContent: 'space-between'}}>
                <Button
                    variant="dialog"
                    color="primary"
                    borderRadius="0 8px 0 8px"
                    onClick={onClose}
                >
                    {t('close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PointsReportDialog;
